import { GlobalEventService } from './event.service';
import { useNotificationStore, AppNotification } from '../stores/notification.store';
import { useWorkplaceStore } from '../stores/workplace.store';
import { Logger } from '../../shared/services/logger.service';
import { BrowserNotificationServiceInstance } from '@/shared/services/browser-notification.service';
import {
  playNotificationSound,
  ROOM_ARRIVAL_SOUND_KEY,
} from '@/shared/services/notification-sound.service';
import {
  arePreferencesReady,
  onPreferencesReady,
  resolveClientChannels,
} from '@/shared/utils/notification-channels.util';
import type {
  ClientNotificationChannels,
  NotificationTypeKey,
} from '@/shared/utils/notification-bucket.util';

/** How a given notification_type should alert, and which preference row governs it. */
interface AlertSpec {
  row: NotificationTypeKey;
  sound: string;
  autoCloseMs?: number;
  /** Set only where another event source plays the same sound for the same moment. */
  dedupeKey?: string;
}

/**
 * Preference rows are keyed exactly as the server stores them — `guests`, not `building`.
 * Reading `building` was a live bug: the settings API saves `guests` (and the backend sends
 * notification_type `guests`), so the client's lookup always missed and fell through to a
 * `?? true` default, meaning Guests Notification preferences were ignored entirely and
 * guest alerts always fired.
 *
 * `room` intentionally uses the same 'knock' sound as OfficeEventRouter. One office arrival
 * emits two socket events, and matching the sound name lets playNotificationSound() collapse
 * them into a single knock instead of two overlapping sounds.
 */
const ALERTS: Record<string, AlertSpec> = {
  room: {
    row: 'room',
    sound: 'knock',
    autoCloseMs: 5000,
    dedupeKey: ROOM_ARRIVAL_SOUND_KEY,
  },
  guests: { row: 'guests', sound: 'channel', autoCloseMs: 7000 },
  webex_message: { row: 'messages', sound: 'chat-notification' },
};

/**
 * Used when a producer omits notification_type (see the `|| 'system'` default below).
 * Alerting under the most general row is preferable to dropping it silently — a missing
 * type is a producer bug, and a gated notification is at least visible and diagnosable.
 */
const FALLBACK_ALERT: AlertSpec = { row: 'room', sound: 'blow', autoCloseMs: 5000 };

class GlobalNotificationService {
  private isInitialized = false;

  public initialize() {
    if (this.isInitialized) {
      Logger.debug('[GlobalNotificationService] Already initialized');
      return;
    }

    // Register the notification worker up front: activation is asynchronous, and without
    // it showNotification() is unavailable — which on Chrome for Android means no browser
    // notification at all, since the `new Notification()` constructor throws there.
    void BrowserNotificationServiceInstance.init();

    GlobalEventService.on('vo.notification.created.topic', this.handleNotificationEvent);

    this.isInitialized = true;
    Logger.debug('[GlobalNotificationService] Initialized');
  }

  private handleNotificationEvent = (event: any) => {
    try {
      const payload = event.data?.payload || event;
      if (!payload) {
        Logger.debug('[GlobalNotificationService] No payload in notification event');
        return;
      }

      const currentUser = useWorkplaceStore.getState().user;
      if (!currentUser?.pid) {
        Logger.debug('[GlobalNotificationService] No current user — ignoring');
        return;
      }
      if (currentUser.pid !== payload.user_pid) {
        Logger.debug('[GlobalNotificationService] Notification is for another user — ignoring');
        return;
      }

      const notification: AppNotification = {
        id: payload.notification_id || `notification_${Date.now()}`,
        type: payload.notification_type || 'system',
        title: payload.title || 'Notification',
        message: payload.message || '',
        data: payload.data || {},
        timestamp: payload.data?.timestamp || new Date().toISOString(),
        read: false,
        userId: currentUser.pid,
      };

      // The notification list (bell) is a record, not an alert, so it is always populated
      // regardless of channel preferences — those govern only sound and the OS bubble.
      useNotificationStore.getState().addNotification(notification);

      this.alert(notification, payload.channels);
    } catch (error) {
      Logger.debug('[GlobalNotificationService] Error processing notification event:', error);
    }
  };

  /**
   * Sound and OS bubble, gated on the resolved channels.
   *
   * When the server has already resolved the channels we act immediately. Otherwise we wait
   * for preferences to load: before that fetch resolves the store holds opt-in placeholders
   * that are all `false`, so evaluating against them would silently discard every
   * notification arriving during startup.
   */
  private alert(notification: AppNotification, serverChannels?: ClientNotificationChannels) {
    const spec = ALERTS[notification.type];
    if (!spec) {
      Logger.debug(
        `[GlobalNotificationService] Unmapped notification_type "${notification.type}" — ` +
          `alerting via the "${FALLBACK_ALERT.row}" row; the producer should send a known type`
      );
    }
    const alertSpec = spec ?? FALLBACK_ALERT;

    const deliver = () => {
      const channels = resolveClientChannels(alertSpec.row, serverChannels);
      Logger.debug(
        `[GlobalNotificationService] "${notification.type}" row=${alertSpec.row} ` +
          `app=${channels.app} browser=${channels.browser}`
      );

      playNotificationSound(alertSpec.sound, channels.app, { dedupeKey: alertSpec.dedupeKey });

      if (channels.browser) {
        void BrowserNotificationServiceInstance.show(notification.title, {
          body: notification.message,
          tag: `${notification.type}-${notification.id}`,
          data: {
            rid: (notification.data as Record<string, unknown>)?.rid,
            url: (notification.data as Record<string, unknown>)?.url,
          },
          autoCloseMs: alertSpec.autoCloseMs,
        });
      }
    };

    if (serverChannels || arePreferencesReady()) deliver();
    else onPreferencesReady(deliver);
  }

  public destroy() {
    if (this.isInitialized) {
      GlobalEventService.off('vo.notification.created.topic', this.handleNotificationEvent);
      this.isInitialized = false;
      Logger.debug('[GlobalNotificationService] Destroyed');
    }
  }

  /**
   * Must be called from a real user gesture. Chrome degrades non-gesture prompts to an
   * easily-missed address-bar chip, Firefox blocks them outright, and Safari requires a
   * gesture — which is why a large cohort ended up permanently unsubscribed having never
   * knowingly seen a prompt.
   */
  public requestNotificationPermission() {
    void BrowserNotificationServiceInstance.requestPermission();
  }
}

export const globalNotificationService = new GlobalNotificationService();
